const STORAGE_PREFIX = 'sacred-scribes-reflection-v1:';

function emptyReflection() {
	return { association: '', notes: '', sightings: [] };
}

function readReflection(key) {
	try {
		const stored = JSON.parse(window.localStorage.getItem(key) || 'null');
		return {
			association: typeof stored?.association === 'string' ? stored.association : '',
			notes: typeof stored?.notes === 'string' ? stored.notes : '',
			sightings: Array.isArray(stored?.sightings) ? stored.sightings : [],
		};
	} catch {
		return emptyReflection();
	}
}

function writeReflection(key, reflection) {
	window.localStorage.setItem(key, JSON.stringify(reflection));
}

function localDateTimeValue(date = new Date()) {
	const local = new Date(date.getTime() - date.getTimezoneOffset() * 60_000);
	return local.toISOString().slice(0, 16);
}

function sightingId() {
	return window.crypto?.randomUUID?.() || `${Date.now()}-${Math.random().toString(16).slice(2)}`;
}

function formatDate(value) {
	const date = new Date(value);
	if (Number.isNaN(date.getTime())) return value;
	return new Intl.DateTimeFormat(undefined, {
		dateStyle: 'medium',
		timeStyle: 'short',
	}).format(date);
}

export default function reflectionJournal() {
	const root = document.querySelector('[data-reflection-journal]');
	if (!root || root.dataset.journalInitialized) return;
	root.dataset.journalInitialized = 'true';

	const reflectionId = root.dataset.reflectionId;
	const form = root.querySelector('[data-reflection-form]');
	const association = root.querySelector('[data-reflection-association]');
	const notes = root.querySelector('[data-reflection-notes]');
	const status = root.querySelector('[data-reflection-status]');
	const sightingForm = root.querySelector('[data-sighting-form]');
	const observedAt = root.querySelector('[data-sighting-date]');
	const context = root.querySelector('[data-sighting-context]');
	const list = root.querySelector('[data-sighting-list]');
	const clearButton = root.querySelector('[data-reflection-clear]');
	if (!reflectionId || !form || !sightingForm || !list) return;

	const storageKey = `${STORAGE_PREFIX}${reflectionId}`;
	let reflection = readReflection(storageKey);
	association.value = reflection.association;
	notes.value = reflection.notes;
	observedAt.value = localDateTimeValue();

	const setStatus = (message) => {
		status.textContent = message;
	};

	const persist = (message) => {
		try {
			writeReflection(storageKey, reflection);
			setStatus(message);
			return true;
		} catch {
			setStatus('This browser could not save the reflection.');
			return false;
		}
	};

	const renderSightings = () => {
		list.innerHTML = '';
		if (!reflection.sightings.length) {
			const empty = document.createElement('li');
			empty.className = 'reflection-journal__empty';
			empty.textContent = 'No sightings saved.';
			list.append(empty);
			return;
		}

		for (const sighting of [...reflection.sightings].reverse()) {
			const item = document.createElement('li');
			const content = document.createElement('div');
			const date = document.createElement('time');
			const description = document.createElement('p');
			const remove = document.createElement('button');
			date.dateTime = sighting.observedAt;
			date.textContent = formatDate(sighting.observedAt);
			description.textContent = sighting.context || 'No context added.';
			remove.type = 'button';
			remove.textContent = '×';
			remove.title = 'Delete sighting';
			remove.setAttribute('aria-label', `Delete sighting from ${date.textContent}`);
			remove.addEventListener('click', () => {
				reflection.sightings = reflection.sightings.filter((entry) => entry.id !== sighting.id);
				persist('Sighting deleted from this browser.');
				renderSightings();
			});
			content.append(date, description);
			item.append(content, remove);
			list.append(item);
		}
	};

	form.addEventListener('submit', (event) => {
		event.preventDefault();
		reflection.association = association.value.trim();
		reflection.notes = notes.value.trim();
		persist('Reflection saved in this browser.');
		clearButton.dataset.confirmClear = '';
		clearButton.textContent = 'Clear saved reflection';
	});

	sightingForm.addEventListener('submit', (event) => {
		event.preventDefault();
		if (!observedAt.value) {
			setStatus('Choose when you noticed the number.');
			return;
		}
		reflection.sightings.push({
			id: sightingId(),
			observedAt: new Date(observedAt.value).toISOString(),
			context: context.value.trim(),
		});
		if (persist('Sighting saved in this browser.')) {
			context.value = '';
			observedAt.value = localDateTimeValue();
			renderSightings();
		}
	});

	clearButton.addEventListener('click', () => {
		if (clearButton.dataset.confirmClear !== 'true') {
			clearButton.dataset.confirmClear = 'true';
			clearButton.textContent = 'Confirm clear';
			setStatus('Select confirm clear to delete this number\'s saved reflection.');
			return;
		}
		reflection = emptyReflection();
		try {
			window.localStorage.removeItem(storageKey);
		} catch {
			setStatus('This browser could not clear the saved reflection.');
			return;
		}
		association.value = '';
		notes.value = '';
		clearButton.dataset.confirmClear = '';
		clearButton.textContent = 'Clear saved reflection';
		setStatus('Saved reflection cleared from this browser.');
		renderSightings();
	});

	renderSightings();
}
